import { localizeUnits } from 'macrotide-shared'

import { LOCALE } from './days'
import { formatInt, round, signed } from './format'

/**
 * Display units. Everything is stored in kg and kcal (sync contract); pages convert at the edges with
 * these helpers, using the units in the SETTINGS profile.
 */
export type WeightUnit = 'kg' | 'lb'
export type EnergyUnit = 'kcal' | 'kj'
/** Height is stored in cm; `ftin` shows it as feet and inches. */
export type HeightUnit = 'cm' | 'ftin'

export interface Units {
  weight: WeightUnit
  energy: EnergyUnit
  height: HeightUnit
}

export const DEFAULT_UNITS: Units = { weight: 'kg', energy: 'kcal', height: 'cm' }

export const KG_PER_LB = 0.45359237
export const KJ_PER_KCAL = 4.184
export const CM_PER_INCH = 2.54
const INCHES_PER_FOOT = 12

// Regions that weigh in pounds and measure height in feet and inches, and those whose food labels lead with kJ.
const IMPERIAL_REGIONS = ['US', 'LR', 'MM']
const KILOJOULE_REGIONS = ['AU', 'NZ']

/** The units most people in [language]'s region use (a BCP 47 tag such as "en-US"), to pre-select in setup. */
export function unitsForLocale(language: string | undefined): Units {
  let region: string | undefined
  try {
    region = language ? new Intl.Locale(language).maximize().region : undefined
  } catch {
    region = undefined
  }
  const imperial = region !== undefined && IMPERIAL_REGIONS.includes(region)
  return {
    weight: imperial ? 'lb' : 'kg',
    energy: region !== undefined && KILOJOULE_REGIONS.includes(region) ? 'kj' : 'kcal',
    height: imperial ? 'ftin' : 'cm',
  }
}

/** [cm] as whole feet and inches to one decimal, carrying 12 in to the next foot: 180 cm → 5 ft 10.9 in. */
export function cmToFeetInches(cm: number): { feet: number; inches: number } {
  const totalInches = Math.round((cm / CM_PER_INCH) * 10) / 10
  const feet = Math.floor(totalInches / INCHES_PER_FOOT)
  return { feet, inches: Number((totalInches - feet * INCHES_PER_FOOT).toFixed(1)) }
}

export function feetInchesToCm(feet: number, inches: number): number {
  return (feet * INCHES_PER_FOOT + inches) * CM_PER_INCH
}

/** A shared message written in kg and kcal (check-in flags, cross-link notes) in [units] (shared UnitText, as on the phone). */
export function localizeMessage(message: string, units: Pick<Units, 'weight' | 'energy'>): string {
  return localizeUnits(message, units.weight === 'lb', units.energy === 'kj')
}

/** "180 cm" or "5 ft 11 in". */
export function formatHeight(cm: number, unit: HeightUnit): string {
  if (unit === 'cm') return `${Math.round(cm)} cm`
  const inches = Math.round(cm / CM_PER_INCH)
  return `${Math.floor(inches / INCHES_PER_FOOT)} ft ${inches % INCHES_PER_FOOT} in`
}

export function weightToDisplay(kg: number, unit: WeightUnit): number {
  return unit === 'lb' ? kg / KG_PER_LB : kg
}

export function weightFromDisplay(value: number, unit: WeightUnit): number {
  return unit === 'lb' ? value * KG_PER_LB : value
}

export function energyToDisplay(kcal: number, unit: EnergyUnit): number {
  return unit === 'kj' ? kcal * KJ_PER_KCAL : kcal
}

export function energyFromDisplay(value: number, unit: EnergyUnit): number {
  return unit === 'kj' ? value / KJ_PER_KCAL : value
}

export function energyLabel(unit: EnergyUnit): string {
  return unit === 'kj' ? 'kJ' : 'kcal'
}

/** [kg] in [unit] rounded to [digits] decimals, as a number for a field or a chart: 81.4, 179.5. */
export function weightDisplay(kg: number, unit: WeightUnit, digits = 1): number {
  return round(weightToDisplay(kg, unit), digits)
}

/** "81.4 kg" or "179.5 lb". */
export function formatWeight(kg: number, unit: WeightUnit, digits = 1): string {
  return `${formatWeightValue(kg, unit, digits)} ${unit}`
}

/** The number alone, for axis ticks and columns headed with the unit: "81.4". */
export function formatWeightValue(kg: number, unit: WeightUnit, digits = 1): string {
  return weightToDisplay(kg, unit).toFixed(digits)
}

/**
 * A training load at gym precision (§4.7): no trailing zeros, at most two decimals, grouped for volumes: "102.5 kg",
 * "20 kg", "12,340 kg".
 */
export function formatLoad(kg: number, unit: WeightUnit): string {
  const value = weightToDisplay(kg, unit)
  return `${value.toLocaleString(LOCALE, { maximumFractionDigits: 2 }).replace('-', '−')} ${unit}`
}

/** A change with its sign and unit: "−0.4 kg", "+1.1 lb". */
export function formatWeightChange(kg: number, unit: WeightUnit, digits = 1): string {
  return `${signed(weightToDisplay(kg, unit), digits)} ${unit}`
}

/** An energy difference with its sign and unit: "−120 kcal", "+400 kJ". */
export function formatEnergyChange(kcal: number, unit: EnergyUnit): string {
  return `${signed(energyToDisplay(kcal, unit), 0)} ${energyLabel(unit)}`
}

/** Whole energy units with grouping and no unit: "2,196", "−120". */
export function formatEnergyValue(kcal: number, unit: EnergyUnit): string {
  return formatInt(energyToDisplay(kcal, unit))
}

/** "2,196 kcal" or "9,188 kJ". */
export function formatEnergy(kcal: number, unit: EnergyUnit): string {
  return `${formatEnergyValue(kcal, unit)} ${energyLabel(unit)}`
}
